import './sp-image-recognition-entry';
import '../inputs/sp-taxon-input/sp-taxon-input';
import '../sp-image/sp-image';
import '../sp-loading-more/sp-loading-more';
import '../sp-id-brand/sp-id-brand';

import {css, html, LitElement, PropertyValues} from 'lit';
import {customElement, property, query, state} from 'lit/decorators.js';
import {
    SpTaxonInputChangeEventPayload,
    SpTaxonInputQueryChangeEventPayload,
    SpUiTaxon
} from "../inputs/sp-taxon-input/sp-taxon-input";
import {translate} from "@stefanholzapfel/lit-translate";
import {when} from "lit/directives/when.js";
import {repeat} from "lit-html/directives/repeat.js";
import {SpScrollbarController} from "../../controllers/sp-scrollbar.controller";
import {perfectScrollbar} from "../../styles/3rd-party/perfect-scrollbar.styles";
import {SpDefaultDialog} from "../sp-dialog/sp-default-dialog";
import {translateUppercase} from "@stefanholzapfel/lit-translate/dist/translateUppercase.directive";

@customElement('sp-image-recognition')
export class SpImageRecognition extends LitElement {

    static styles = [
        perfectScrollbar,
        // language=CSS
        css`
            :host {
                display: grid;
                grid-template-rows: auto auto 1fr auto;
                grid-auto-flow: row;
                grid-row-gap: var(--sp-default-padding);
                width: 100%;
                height: 100%;
                line-height: var(--sp-default-line-height);
            }
            
            .image-container {
                display: flex;
                flex-direction: column;
                row-gap: var(--sp-default-gap);
            }

            .image-container sp-image {
                height: unset;
            }
            
            .img-primary {
                aspect-ratio: 4 / 3;
                border-radius: var(--sp-default-border-radius);
            }

            /* This is a best effort approach for legacy browsers where heights are hardcoded */
            @supports not (aspect-ratio: 1) {
                .img-primary {
                    height: 360px;
                }
            }
            
            .no-image {
                place-self: center;
                font-size: var(--sp-font-size-headline);
            }
            
            .list-info, .loading-info {
                font-size: var(--sp-font-size-headline);
            }
            
            .list-container {
                position: relative;
            }

            sp-image-recognition-entry {
                cursor: pointer;
            }

            sp-image-recognition-entry:first-of-type {
                padding-top: calc(var(--sp-default-padding) - var(--sp-default-gap));
            }
            
            sp-image-recognition-entry:not(:first-of-type) {
                padding-top: var(--sp-default-padding);
            }
            
            sp-image-recognition-entry:not(:last-of-type) {
                border-bottom: 1px solid rgb(var(--sp-greylight));
                box-shadow: var(--sp-default-box-shadow);
                padding-bottom: var(--sp-default-padding);
            }
        
            sp-default-dialog {
                display: none;
            }
            
            sp-default-dialog::part(header) {
                background-color: unset;
            }
        `
    ]

    @property({type: Array})
    images: SpImageRecognitionImage[] = [];
    @property({type: Boolean})
    allowManualClassification = true;

    @property({type: Function})
    getPredictionsCallback: SpImageRecognitionGetPredictions;
    @property({type: Function})
    getTaxaForPredictionsCallback: SpImageRecognitionGetTaxaForPredictions;
    @property({type: Function})
    getTaxaForManualFieldCallback: SpImageRecognitionGetTaxaForManualField;

    @state()
    activeImage: SpImageRecognitionImage;
    @state()
    viewablePredictionSets: SpImageRecognitionViewablePredictionSet[] = [];
    @state()
    activeViewablePredictionSet: SpImageRecognitionViewablePredictionSet;
    @state()
    predictionsLoading = false;
    @state()
    taxonInfoEntry: SpUiTaxon;
    @state()
    taxonList: SpUiTaxon[] = [];
    @state()
    taxonListLoading = false;

    @query('.list-container')
    listContainer;

    @query('sp-default-dialog')
    dialog: SpDefaultDialog;

    predictionsAbortController = new AbortController();
    viewablePredictionsAbortController = new AbortController();
    taxonAbortController = new AbortController();
    scrollbarController = new SpScrollbarController(this);

    protected async willUpdate(_changedProperties: PropertyValues) {
        super.willUpdate(_changedProperties);
        if (_changedProperties.has('images') && Array.isArray(this.images) && this.images.length > 0) {
            this.select(this.images[0]);
        }
    }

    protected updated(_changedProperties: PropertyValues) {
        super.updated(_changedProperties);
        if (_changedProperties.has('taxonInfoEntry') && this.taxonInfoEntry) {
            this.dialog.show();
        }
        if (_changedProperties.has('images') && Array.isArray(this.images) && this.images.length > 0) {
            this.scrollbarController.add(this.listContainer, {psOptions: {suppressScrollX: true}});
        }
    }

    protected render() {
        return this.images && this.images.length > 0 ?
             html`
                <sp-default-dialog
                    fullHeight
                    .template=${html`<sp-taxon-info .taxon=${this.taxonInfoEntry}></sp-taxon-info>`}
                    .buttons=${[
                        {
                            text: translateUppercase('UI_COMPONENT_OK'),
                            textPosition: 'center',
                            clickCallback: () => {
                                this.selectPrediction({
                                    type: 'prediction',
                                    value: this.taxonInfoEntry 
                                });
                                this.dialog.hide();
                            }
                        },
                        {
                            text: translateUppercase('UI_COMPONENT_BACK'),
                            textPosition: 'center',
                            clickCallback: () => {
                                this.dialog.hide();
                            }
                        }
                    ]}
                    @after-hide=${ () => this.taxonInfoEntry = null }></sp-default-dialog>
                <div class="image-container">
                    <sp-id-brand></sp-id-brand>
                    <sp-image class="img-primary" .image=${ Array.isArray(this.images) && this.images[0] ? this.images[0].data : null }></sp-image>
                </div>
                <div>
                    ${ when(this.activeViewablePredictionSet?.predictions?.length > 0, 
                        () => html`<span class="list-info">${ translate("UI_COMPONENT_IMAGE_RECOGNITION_LIST_INFO") }</span>`,
                        () => {
                            return this.predictionsLoading ?
                                html`<span class="loading-info">${ translate("UI_COMPONENT_IMAGE_RECOGNITION_LIST_LOADING") }</span>` :
                                html`<span class="loading-info">${ translate("UI_COMPONENT_IMAGE_RECOGNITION_NOTHING_FOUND") }</span>`
                        }) }
                </div>
                <div class="list-container">
                    ${ when(this.activeViewablePredictionSet?.predictions, () => repeat(this.activeViewablePredictionSet.predictions, prediction => 
                        when(prediction.taxon, () => 
                            html`<sp-image-recognition-entry 
                                    .viewablePrediction=${ prediction }
                                    @click=${ () => {
                                        this.selectPrediction({
                                            type: 'prediction',
                                            value: prediction.taxon
                                        });
                                    }}
                                    @taxon-info=${ e => this.taxonInfoEntry = e.detail.value }></sp-image-recognition-entry>`))
                    )}
                    ${ when(this.predictionsLoading, () => html`<sp-loading-more></sp-loading-more>`) }
                </div>
                ${ when(this.allowManualClassification, () => html`
                    <sp-taxon-input
                            .taxonList=${ this.taxonList}
                            .taxonListLoading=${ this.taxonListLoading }
                            .label=${ translate('UI_COMPONENT_IMAGE_RECOGNITION_TAXON_LABEL') }
                            @change=${ (e: CustomEvent<SpTaxonInputChangeEventPayload>) => {
                                this.selectPrediction({
                                    type: 'prediction',
                                    value: e.detail.value
                                });
                            } }
                            @change-query=${this.loadTaxaForManualField}>
                    </sp-taxon-input>`)}` :
            html`<span class="no-image">${ translate("UI_COMPONENT_IMAGE_RECOGNITION_NO_IMAGE") }</span>`
    }

    selectPrediction(payload: SpImageRecognitionSelectEventPayload) {
        this.dispatchEvent(new CustomEvent<SpImageRecognitionSelectEventPayload>('select', {
            detail: payload,
            bubbles: true,
            cancelable: false,
            composed: true,
        }));
    }

    async loadTaxaForManualField(e: CustomEvent<SpTaxonInputQueryChangeEventPayload>) {
        if (!this.getPredictionsCallback || !this.getTaxaForPredictionsCallback) {
            throw 'Provide getTaxaForManualField callback!';
        }
        this.taxonAbortController.abort();
        this.taxonAbortController = new AbortController();
        this.taxonListLoading = true;
        try {
            this.taxonList = await this.getTaxaForManualFieldCallback(e.detail.value, this.taxonAbortController);
            // TODO: handle errors?
        } finally {
            this.taxonListLoading = false;
        }
    }

    async select(image: SpImageRecognitionImage, predictionSet?: SpImageRecognitionViewablePredictionSet) {
        this.activeImage = image;
        const existingPredictionSet = this.viewablePredictionSets.find(prediction => prediction.id === image.id);
        if (!existingPredictionSet) {
            if (!this.getPredictionsCallback || !this.getTaxaForPredictionsCallback) {
                throw 'Provide getPredictions and getTaxaForPredictions callbacks!';
            }
            this.predictionsAbortController.abort();
            this.predictionsAbortController = new AbortController();
            this.viewablePredictionsAbortController.abort();
            this.viewablePredictionsAbortController = new AbortController();
            this.predictionsLoading = true;
            try {
                let predictionSets = await this.getPredictionsCallback(this.activeImage.id, this.activeImage, this.predictionsAbortController);
                const predictionResults = predictionSets
                    .flatMap(set => set.predictions)
                    .map(pred => pred.result);
                const taxa = await this.getTaxaForPredictionsCallback(predictionResults, this.viewablePredictionsAbortController);
                const viewAblePredictionSets = (predictionSets as SpImageRecognitionViewablePredictionSet[]).map(predictionSet => {
                    predictionSet.predictions = predictionSet.predictions
                        .map(prediction => {
                            prediction.taxon = taxa.find(taxon => taxon?.taxon_name === prediction.result);
                            return prediction;
                        })
                        .filter(prediction => !!prediction.taxon)
                    return predictionSet;
                });
                this.viewablePredictionSets = [
                    ...this.viewablePredictionSets,
                    ...viewAblePredictionSets
                ];
                this.activeViewablePredictionSet = viewAblePredictionSets[0];
                // TODO: handle errors?
            } finally {
                this.predictionsLoading = false;
            }
        } else {
            this.activeViewablePredictionSet = predictionSet ?? existingPredictionSet;
        }
    }
}

// Internal interfaces
export interface SpImageRecognitionPredictionSet {
    id: string;
    predictions: SpImageRecognitionPrediction[],
    boundingBox?: {
        left: number;
        right: number;
        width: number;
        height: number;
    }
}
export type SpImageRecognitionViewablePredictionSet = Omit<SpImageRecognitionPredictionSet, 'predictions'> & {
    predictions: SpImageRecognitionViewablePrediction[]
}
export interface SpImageRecognitionPrediction {
    result: string;
    probability: number;
}
export type SpImageRecognitionViewablePrediction = SpImageRecognitionPrediction & { taxon: SpUiTaxon }

// Property interfaces
export interface SpImageRecognitionImage {
    id: string
    data: string | Blob
}
export type SpImageRecognitionGetPredictions = (imageId: string, image: SpImageRecognitionImage, abortController: AbortController) => Promise<SpImageRecognitionPredictionSet[]>;
export type SpImageRecognitionGetTaxaForPredictions = (predictionResults: string[], abortController: AbortController) => Promise<SpUiTaxon[]>;
export type SpImageRecognitionGetTaxaForManualField = (query: string, abortController: AbortController) => Promise<SpUiTaxon[]>;

// Event
export type SpImageRecognitionSelectEvent = CustomEvent<SpImageRecognitionSelectEventPayload>;

// Event payloads
export interface SpImageRecognitionSelectEventPayload {
    type: 'manual' | 'prediction';
    field?: 'taxo';
    value: SpUiTaxon;
}